import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@shared/auth/AuthContext'
import { apiConfig } from '@shared/config/environment'
import { setCsrfToken } from '@shared/services/ApiClient'
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material'
import {
  Email,
  CheckCircle,
  Refresh,
  ArrowBack,
  ErrorOutline
} from '@mui/icons-material'

export default function EmailVerificationPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { } = useAuth()

  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending')
  const [verificationMessage, setVerificationMessage] = useState('')

  // Get email from sessionStorage (set during signup) or fallback to URL param
  const email = sessionStorage.getItem('signupEmail') || searchParams.get('email') || ''
  const token = searchParams.get('token')

  useEffect(() => {
    // If there's a token in the URL, automatically verify it
    if (token) {
      handleEmailVerification(token)
    }
  }, [token])

  const handleEmailVerification = async (verificationToken: string) => {
    setVerificationStatus('verifying')

    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/verify-email/${verificationToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })

      const result = await response.json()

      if (result.success) {
        const csrfToken = result.data?.csrfToken
        if (csrfToken) {
          setCsrfToken(csrfToken)
        }

        setVerificationStatus('success')
        setVerificationMessage(result.message)

        // Redirect to login after a brief delay to allow user to see success message
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        setVerificationStatus('error')
        setVerificationMessage(result.message || 'Verification failed')
      }
    } catch (error) {
      setVerificationStatus('error')
      setVerificationMessage('An error occurred during verification')
    }
  }

  const handleResendVerification = async () => {
    if (!email) return

    setIsResending(true)
    setResendError(null)
    setResendSuccess(false)

    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })

      const result = await response.json()

      if (result.success) {
        setResendSuccess(true)
      } else {
        setResendError(result.message || 'Failed to resend verification email')
      }
    } catch (error) {
      setResendError('An error occurred while resending verification email')
    } finally {
      setIsResending(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  // If verifying token
  if (verificationStatus === 'verifying') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CardContent>
              <CircularProgress size={64} sx={{ color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Verifying Your Email
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your email address...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }

  // If verification successful
  if (verificationStatus === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CardContent>
              <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Email Verified!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                {verificationMessage}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Your 14-day free trial has started. Redirecting you to login...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }

  // If verification failed
  if (verificationStatus === 'error') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CardContent>
              <ErrorOutline sx={{ fontSize: 64, color: theme.palette.error.main, mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {verificationMessage}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={handleBackToLogin}>
                  Back to Login
                </Button>
                {email && (
                  <Button
                    variant="contained"
                    onClick={handleResendVerification}
                    disabled={isResending}
                  >
                    {isResending ? <CircularProgress size={20} /> : 'Resend Email'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }

  // Default: Check your email page
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToLogin}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.1)
              }
            }}
          >
            Back to Login
          </Button>
        </Box>

        <Card sx={{ textAlign: 'center', p: 4 }}>
          <CardContent>
            <Email sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Check Your Email
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a verification link to {email}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Click the link in your email to verify your account and start your 14-day free trial.
              The link will expire in 24 hours.
            </Typography>

            {resendSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Verification email sent! Check your inbox.
              </Alert>
            )}

            {resendError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {resendError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleResendVerification}
                disabled={isResending || !email}
                sx={{ minWidth: 160 }}
              >
                {isResending ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Resend Email'
                )}
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Didn't receive the email? Check your spam folder or try resending.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
